import { appConfirm } from '../services/appDialogs';
import React, { useState, useEffect, useCallback } from 'react';
import { Film, Plus, Play, Trash2, X } from 'lucide-react';
import MotionStoryboardEditor from './canvas-mode/MotionStoryboardEditor';
import { useTheme } from '../context/ThemeContext';
import { useDelayedUnmount } from '../hooks/useDelayedUnmount';

const STORAGE_KEY = 'motion_storyboard_projects';

// 从 localStorage 读取项目列表（不含帧数据，帧数据单独存）
function loadProjects() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveProjects(projects) {
  // 存索引时去掉 savedFrames（太大），帧数据单独存
  const slim = projects.map(({ savedFrames, ...rest }) => rest);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(slim));
}

function loadFrames(projectId) {
  try {
    const raw = localStorage.getItem(`motion_frames_${projectId}`);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function saveFramesData(projectId, frames) {
  if (!frames || frames.length === 0) return;
  // 只保存 fabricJson 和 isKeyFrame，thumbnail 太大不存
  const slim = frames.map(f => ({
    index: f.index,
    isKeyFrame: f.isKeyFrame,
    fabricJson: f.fabricJson,
  }));
  try {
    localStorage.setItem(`motion_frames_${projectId}`, JSON.stringify(slim));
  } catch (e) {
    console.warn('帧数据保存失败（可能超出 localStorage 限制）:', e);
  }
}

function deleteFramesData(projectId) {
  localStorage.removeItem(`motion_frames_${projectId}`);
}

/**
 * 动态分镜页面 - 独立入口（从"更多"菜单进入）
 */
export default function MotionStoryboardPage({ projectId, episodeId }) {
  const { getTheme } = useTheme();
  const theme = getTheme();

  const [projects, setProjects] = useState(() => loadProjects());
  const [currentEditor, setCurrentEditor] = useState(null);
  const [previewVideo, setPreviewVideoRaw] = useState(null);
  const [lastPreviewVideo, setLastPreviewVideo] = useState(null); // 出场动画期间保留数据
  const setPreviewVideo = useCallback((v) => {
    if (v) setLastPreviewVideo(v);
    setPreviewVideoRaw(v);
  }, []);
  const previewModal = useDelayedUnmount(!!previewVideo, 150);
  const activePreviewVideo = previewVideo || lastPreviewVideo;

  // 项目列表变化时持久化
  useEffect(() => {
    saveProjects(projects);
  }, [projects]);

  // 创建新项目
  const handleCreateNew = () => {
    const newProject = {
      id: `motion-${Date.now()}`,
      name: `动态分镜 ${projects.length + 1}`,
      width: 1920,
      height: 1080,
      fps: 24,
      frameCount: 1,
      exportedVideoUrl: null,
      thumbnail: null,
      createdAt: new Date().toISOString(),
    };
    setProjects([newProject, ...projects]);
    setCurrentEditor({ id: newProject.id, data: newProject });
  };

  // 编辑器保存回调（关闭或导出时触发）
  const handleSave = useCallback((updatedData) => {
    const id = currentEditor?.id;
    if (!id) return;

    // 持久化帧数据
    if (updatedData.savedFrames) {
      saveFramesData(id, updatedData.savedFrames);
    }

    // 更新项目列表（不含 savedFrames）
    const { savedFrames, ...projectData } = updatedData;
    setProjects(prev => prev.map(p => p.id === id ? { ...p, ...projectData } : p));
    setCurrentEditor(null);
  }, [currentEditor]);

  // 打开编辑器（加载已保存的帧数据）
  const handleOpenEditor = (project) => {
    const savedFrames = loadFrames(project.id);
    setCurrentEditor({
      id: project.id,
      data: { ...project, savedFrames },
    });
  };

  // 删除项目
  const handleDelete = async (e, projectId) => {
    e.stopPropagation();
    if (!(await appConfirm('确定删除这个动态分镜项目吗？'))) return;
    setProjects(prev => prev.filter(p => p.id !== projectId));
    deleteFramesData(projectId);
  };

  return (
    <div className="w-full h-full flex flex-col" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
      {/* 头部 */}
      <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--color-border-primary)' }}>
        <div className="flex items-center gap-3">
          <Film size={24} style={{ color: theme.colors.accentPrimary }} />
          <div>
            <h1 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>动态分镜</h1>
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>逐帧绘制动画，导出为参考视频</p>
          </div>
        </div>
        <button
          onClick={handleCreateNew}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-[var(--color-accent-contrast)] font-medium transition-all hover:opacity-90"
          style={{ backgroundColor: theme.colors.accentPrimary }}
        >
          <Plus size={18} />
          新建动态分镜
        </button>
      </div>

      {/* 项目列表 */}
      <div className="flex-1 overflow-auto p-6">
        {projects.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-4">
            <Film size={64} style={{ color: 'var(--color-text-muted)', opacity: 0.3 }} />
            <p className="text-lg" style={{ color: 'var(--color-text-muted)' }}>还没有动态分镜项目</p>
            <button
              onClick={handleCreateNew}
              className="flex items-center gap-2 px-6 py-3 rounded-lg text-[var(--color-accent-contrast)] font-medium transition-all hover:opacity-90"
              style={{ backgroundColor: theme.colors.accentPrimary }}
            >
              <Plus size={20} />
              创建第一个项目
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {projects.map(project => (
              <div
                key={project.id}
                className="rounded-lg border overflow-hidden cursor-pointer transition-all hover:shadow-lg group relative"
                style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-secondary)' }}
                onClick={() => handleOpenEditor(project)}
              >
                {/* 缩略图 */}
                <div className="aspect-video flex items-center justify-center relative" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
                  {project.thumbnail ? (
                    <img src={project.thumbnail} alt={project.name} className="w-full h-full object-cover" />
                  ) : (
                    <Film size={48} style={{ color: 'var(--color-text-muted)', opacity: 0.3 }} />
                  )}

                  {/* 播放按钮（有导出视频时显示） */}
                  {project.exportedVideoUrl && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setPreviewVideo(project); }}
                      className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="播放导出的视频"
                    >
                      <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                        <Play size={24} className="ml-1" style={{ color: 'var(--color-accent-primary)' }} />
                      </div>
                    </button>
                  )}

                  {/* 删除按钮 */}
                  <button
                    onClick={(e) => handleDelete(e, project.id)}
                    className="absolute top-2 right-2 p-1.5 rounded-full bg-[var(--overlay-light)] text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[var(--color-error)]"
                    title="删除项目"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>

                {/* 信息 */}
                <div className="p-4">
                  <h3 className="font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>{project.name}</h3>
                  <div className="flex items-center justify-between text-xs" style={{ color: 'var(--color-text-muted)' }}>
                    <span>{project.frameCount || 1} 帧</span>
                    <span>{project.width}×{project.height}</span>
                    <span>{project.fps} fps</span>
                  </div>
                  {project.exportedVideoUrl && (
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex-1 px-2 py-1 rounded text-xs text-center" style={{ backgroundColor: 'var(--color-success)', color: 'var(--color-accent-contrast)' }}>
                        已导出视频
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); setPreviewVideo(project); }}
                        className="px-2 py-1 rounded text-xs text-[var(--color-accent-contrast)] transition-opacity hover:opacity-90"
                        style={{ backgroundColor: 'var(--color-accent-primary)' }}
                      >
                        播放
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 视频播放弹窗 */}
      {previewModal.mounted && activePreviewVideo && (
        <div className={`fixed inset-0 z-[9998] flex items-center justify-center bg-[var(--overlay-medium)] ${previewModal.exiting ? 'animate-out fade-out duration-150' : 'animate-in fade-in duration-150'}`} onClick={() => setPreviewVideo(null)}>
          <div
            className={`relative rounded-lg overflow-hidden shadow-2xl ${previewModal.exiting ? 'animate-out fade-out zoom-out-95 duration-150' : 'animate-in fade-in zoom-in-95 duration-200'}`}
            style={{ maxWidth: '80vw', maxHeight: '80vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            <video
              src={activePreviewVideo.exportedVideoUrl}
              className="w-full h-full"
              style={{ maxHeight: '80vh', objectFit: 'contain' }}
              controls
              autoPlay
              loop
            />
            <button
              onClick={() => setPreviewVideo(null)}
              className="absolute top-3 right-3 p-2 rounded-full bg-[var(--overlay-medium)] text-white hover:bg-[var(--overlay-heavy)] transition-all"
              title="关闭"
            >
              <X size={18} />
            </button>
            <div className="absolute bottom-3 left-3 px-3 py-1.5 rounded-lg bg-[var(--overlay-medium)] text-white text-sm">
              {activePreviewVideo.name}
            </div>
          </div>
        </div>
      )}

      {/* 编辑器 */}
      {currentEditor && (
        <MotionStoryboardEditor
          isOpen={true}
          nodeData={currentEditor.data}
          projectId={projectId}
          episodeId={episodeId}
          onSave={handleSave}
          onClose={() => setCurrentEditor(null)}
        />
      )}
    </div>
  );
}
